import type { MouseEvent } from "react";
import type { StrandLossOverlaySegment } from "../../helpers/mapStrandLossOverlays";
import { strandLossColors } from "../../theme/colors";
import type { StrandLoss } from "../../types/strand-loss";
import { StrandLossTooltip } from "./StrandLossTooltip";

interface StrandLossOverlayProps {
  segments: StrandLossOverlaySegment[];
  fontFamily: string;
  fontSize: string;
  passive?: boolean;
  onLossContextMenu?: (
    event: MouseEvent<SVGGElement>,
    loss: StrandLoss,
  ) => void;
  onLossDoubleClick?: (loss: StrandLoss) => void;
}

const JUSTIFIED_PATTERN_ID = "gantt-strand-loss-stripes";
const UNDECLARED_PATTERN_ID = "gantt-strand-loss-undeclared-stripes";
const STRIPE_SIZE = 8;
const STRIPE_WIDTH = 4;

export const StrandLossOverlay = ({
  segments,
  fontFamily,
  fontSize,
  onLossContextMenu,
  onLossDoubleClick,
  passive = false,
}: StrandLossOverlayProps) => {
  if (segments.length === 0) return null;

  return (
    <g>
      <defs>
        <pattern
          id={JUSTIFIED_PATTERN_ID}
          patternUnits="userSpaceOnUse"
          width={STRIPE_SIZE}
          height={STRIPE_SIZE}
          patternTransform="rotate(45)"
        >
          <rect
            width={STRIPE_SIZE}
            height={STRIPE_SIZE}
            fill={strandLossColors.background}
          />
          <rect
            width={STRIPE_WIDTH}
            height={STRIPE_SIZE}
            fill={strandLossColors.warning}
          />
        </pattern>
        <pattern
          id={UNDECLARED_PATTERN_ID}
          patternUnits="userSpaceOnUse"
          width={STRIPE_SIZE}
          height={STRIPE_SIZE}
          patternTransform="rotate(45)"
        >
          <rect
            width={STRIPE_SIZE}
            height={STRIPE_SIZE}
            fill={strandLossColors.background}
          />
          <rect
            width={STRIPE_WIDTH}
            height={STRIPE_SIZE}
            fill={strandLossColors.undeclared}
          />
        </pattern>
      </defs>
      {segments.map(({ id, loss, x, y, width, height }) => (
        <StrandLossTooltip
          key={id}
          loss={loss}
          fontFamily={fontFamily}
          fontSize={fontSize}
        >
          <g
            role="img"
            aria-label={`Strand #${loss.Strand} loss`}
            onDoubleClick={
              passive
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    onLossDoubleClick?.(loss);
                  }
            }
            onContextMenu={
              passive
                ? undefined
                : (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onLossContextMenu?.(event, loss);
                  }
            }
          >
            <rect
              data-strand-loss-id={id}
              x={x}
              y={y}
              width={width}
              height={height}
              fill={`url(#${loss.DelayCodeName?.trim() ? JUSTIFIED_PATTERN_ID : UNDECLARED_PATTERN_ID})`}
              stroke={strandLossColors.background}
              strokeWidth={0.5}
            />
          </g>
        </StrandLossTooltip>
      ))}
    </g>
  );
};
